import "../instrument"
import React, { createContext, useContext, type ReactNode } from "react"
import { BrowserRouter } from "react-router"
import { QueryClient } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { HelmetProvider, Helmet } from "react-helmet-async"
import { SidebarProvider } from "./SidebarContext"
import { AxiosInterceptorWrapper } from "./AxiosInterceptor"
import { DrawerProvider } from "./DrawerContext"
import { ToastProvider } from "./ToastContext"
import "../i18n"
import { Toaster } from "@components/ui/sonner"
import { AppDrawer } from "@components/AppDrawer"
import { MS_IN_DAY } from "@constants"
import { Auth0ProviderWithNavigate } from "./Auth0ProviderWithNavigate"


// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AppProviderContextType {
  // Shared context methods and properties
}

const AppProviderContext = createContext<AppProviderContextType>({})

const useAppProvider = () => useContext(AppProviderContext)

interface AppProvidersProps {
  children: ReactNode
}

const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const contextValue: AppProviderContextType = {}
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Garbage collection is set to a longer time to match the persister's maxAge default
        // This ensures garbage collection won't kick in and discard the persisted data earlier than expected
        gcTime: MS_IN_DAY,
        refetchOnWindowFocus: false,
      },
    },
  })
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
  })

  return (
    <AppProviderContext.Provider value={contextValue}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              return (
                query.meta?.persist === true && query.state.status === "success"
              )
            },
          },
        }}
      >
        <BrowserRouter>
          <Auth0ProviderWithNavigate>
            <AxiosInterceptorWrapper>
              <HelmetProvider>
                <Helmet
                  titleTemplate="%s | Inspire CRM"
                  defaultTitle="Inspire CRM"
                />
                <SidebarProvider>
                  <DrawerProvider>
                    <ToastProvider>
                      <AppDrawer />
                      <Toaster />
                      {children}
                    </ToastProvider>
                  </DrawerProvider>
                </SidebarProvider>
              </HelmetProvider>
            </AxiosInterceptorWrapper>
          </Auth0ProviderWithNavigate>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    </AppProviderContext.Provider>
  )
}

export { AppProviders, useAppProvider }
